// Toolbar above the table: global search, optional date range, bulk-actions slot.
// Search reads state.pendingSearch (instant) and writes through state.setSearch (debounced).

import type { ChangeEvent, JSX, ReactNode } from "react";
import type { DataTableState } from "./types.js";

const INPUT_CLS = "text-sm rounded-lg border border-slate-300 bg-white px-3 py-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500";
const LABEL_CLS = "flex items-center gap-2 text-sm text-slate-600";

interface Props {
  state: DataTableState;
  searchPlaceholder?: string;
  enableDateRange?: boolean;
  bulkActions?: (selectedIds: readonly string[]) => ReactNode;
}

function SearchInput({ state, placeholder }: { state: DataTableState; placeholder: string }): JSX.Element {
  return (
    <label className={LABEL_CLS}>
      <span className="sr-only">Search</span>
      <input type="search" className={`${INPUT_CLS} w-64`} placeholder={placeholder}
        value={state.pendingSearch}
        onChange={(e: ChangeEvent<HTMLInputElement>) => state.setSearch(e.target.value)}
        aria-label="Global search" />
    </label>
  );
}

function DateRange({ state }: { state: DataTableState }): JSX.Element {
  const from = state.query.dateFrom ?? "";
  const to = state.query.dateTo ?? "";
  return (
    <div className="flex items-center gap-2">
      <label className={LABEL_CLS}>
        <span>From</span>
        <input type="date" className={INPUT_CLS} value={from} aria-label="Date from"
          onChange={(e) => state.setDateRange(e.target.value || undefined, to || undefined)} />
      </label>
      <label className={LABEL_CLS}>
        <span>To</span>
        <input type="date" className={INPUT_CLS} value={to} aria-label="Date to"
          onChange={(e) => state.setDateRange(from || undefined, e.target.value || undefined)} />
      </label>
    </div>
  );
}

function BulkBar({ state, render }: {
  state: DataTableState;
  render: (ids: readonly string[]) => ReactNode;
}): JSX.Element | null {
  const ids = Array.from(state.selection.selected);
  if (ids.length === 0) return null;
  return (
    <div role="region" aria-label="Bulk actions" className="flex items-center gap-3 px-3 py-2 rounded-lg bg-navy-50 border border-navy-200">
      <span className="text-sm text-navy-900"><strong>{ids.length}</strong> selected</span>
      <button type="button" onClick={state.selection.clear}
        className="text-xs text-navy-800 hover:text-navy-950 underline">Clear</button>
      <div className="ml-auto flex items-center gap-2">{render(ids)}</div>
    </div>
  );
}

export function DataTableToolbar({ state, searchPlaceholder, enableDateRange, bulkActions }: Props): JSX.Element {
  return (
    <div className="space-y-2 mb-3">
      <div className="flex flex-wrap items-center gap-3">
        <SearchInput state={state} placeholder={searchPlaceholder ?? "Search…"} />
        {enableDateRange && <DateRange state={state} />}
      </div>
      {bulkActions && <BulkBar state={state} render={bulkActions} />}
    </div>
  );
}
